import React, { useState, useRef, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import { ADMIN_ROLES } from "../../constants/adminPermissions";
import { ROUTES } from "../../constants/routes";
import { Shield, Users, ChevronDown, Check, GraduationCap } from "lucide-react";
import "./AdminLayout.css";

export default function AdminLayout() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isUserSwitcherOpen, setIsUserSwitcherOpen] = useState(false);
  const { role, adminUser, switchRole } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const switcherRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (switcherRef.current && !switcherRef.current.contains(event.target)) {
        setIsUserSwitcherOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Compute title from route for the header center
  const getPageTitle = () => {
    const p = location.pathname;
    if (p.includes("/admin/questions/bank")) return "Question Bank";
    if (p.includes("/admin/questions/reports")) return "Question Reports";
    if (p.includes("/admin/tests/builder")) return "Test Builder";
    if (p.includes("/admin/tests/management")) return "Test Management";
    if (p.includes("/admin/users")) return "User Management";
    if (p.includes("/admin/mentors/assignment")) return "Mentor User Assignment";
    if (p.includes("/admin/mentors/notifications")) return "Notifications";
    if (p.includes("/admin/mentors/reports")) return "Mentor Reports";
    if (p.includes("/admin/analytics/users")) return "User Analytics";
    if (p.includes("/admin/analytics/tests")) return "Test Analytics";
    if (p.includes("/admin/analytics/payments")) return "Payments & Subscriptions";
    if (p.includes("/admin/system/users")) return "Admin Users";
    if (p.includes("/admin/system/roles")) return "Roles & Permissions";
    if (p.includes("/admin/system/audit-logs")) return "Audit Logs";
    return "Admin";
  };

  const getRoleClass = () => {
    switch (role) {
      case ADMIN_ROLES.SUPER_ADMIN:
        return "super-admin";
      case ADMIN_ROLES.ACADEMICS:
        return "academics";
      case ADMIN_ROLES.OPERATIONS:
        return "operations";
      case ADMIN_ROLES.SUPPORT:
        return "support";
      default:
        return "";
    }
  };

  const roleOptions = [
    {
      id: ADMIN_ROLES.SUPER_ADMIN,
      label: "Super Admin",
      description: "Full access across all modules",
      avatarLetter: "SA",
    },
    {
      id: ADMIN_ROLES.ACADEMICS,
      label: "Academics",
      description: "Question Bank & Test Management",
      avatarLetter: "AC",
    },
    {
      id: ADMIN_ROLES.OPERATIONS,
      label: "Operations",
      description: "Mentors, Analytics & Operations",
      avatarLetter: "OP",
    },
    {
      id: ADMIN_ROLES.SUPPORT,
      label: "Support",
      description: "User assistance & basic accounts",
      avatarLetter: "SU",
    },
  ];

  return (
    <div className="app-layout admin-app-layout">
      {/* 1. Global Top Header matching Yooki Header architecture */}
      <header className="header-div admin-global-header">
        <div className="div-left-header admin-header-left">
          {/* Authentic Single Yooki Logo */}
          <img
            src="/assets/images/logo/SVG/Logo-Org.svg"
            alt="Yooki"
            className="header-logo"
            onClick={() => navigate(ROUTES.ADMIN_QUESTIONS_BANK)}
            style={{ cursor: "pointer" }}
          />

          {/* Just Admin title */}
          <span className="admin-header-title">Admin</span>

          {/* Switch User dropdown button directly beside Admin */}
          <div className="admin-switch-user-wrapper" ref={switcherRef}>
            <button
              className="admin-switch-user-btn"
              onClick={() => setIsUserSwitcherOpen((prev) => !prev)}
              title="Switch user role & permissions"
            >
              <Users size={14} className="admin-switch-user-icon" />
              <span>Switch User</span>
              <ChevronDown size={13} />
            </button>

            {isUserSwitcherOpen && (
              <div className="admin-switch-user-dropdown">
                <div className="admin-switch-user-dropdown-header">
                  <span>Switch Role</span>
                </div>
                {roleOptions.map((opt) => (
                  <button
                    key={opt.id}
                    className={`admin-switch-user-option ${role === opt.id ? "selected" : ""}`}
                    onClick={() => {
                      switchRole(opt.id);
                      setIsUserSwitcherOpen(false);
                    }}
                  >
                    <div className="admin-user-option-avatar">{opt.avatarLetter}</div>
                    <div className="admin-user-option-info">
                      <span className="admin-user-option-title">{opt.label}</span>
                      <span className="admin-user-option-desc">{opt.description}</span>
                    </div>
                    {role === opt.id && <Check size={16} className="admin-user-option-check" />}
                  </button>
                ))}

                {/* Direct switch to Student View option inside Switch User */}
                <div className="admin-switch-user-divider" />
                <button
                  className="admin-switch-user-option student-option"
                  onClick={() => {
                    setIsUserSwitcherOpen(false);
                    navigate(ROUTES.DASHBOARD);
                  }}
                  title="Switch to Student App view"
                >
                  <div className="admin-user-option-avatar student-avatar">
                    <GraduationCap size={15} />
                  </div>
                  <div className="admin-user-option-info">
                    <span className="admin-user-option-title">Student View</span>
                    <span className="admin-user-option-desc">Candidate practice portal</span>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Center: Current Module Name */}
        <div className="div-center-header">
          <span className="text-center-header admin-center-title">{getPageTitle()}</span>
        </div>

        {/* Right: Active Role Badge */}
        <div className="div-right-header admin-header-right">
          <div className={`admin-role-badge ${getRoleClass()}`}>
            <Shield size={13} />
            {role || "SUPER_ADMIN"}
          </div>
        </div>
      </header>

      {/* 2. Body below the fixed 45px header */}
      <div className="app-body admin-app-body">
        <AdminSidebar
          isCollapsed={isCollapsed}
          onToggleCollapse={() => setIsCollapsed((prev) => !prev)}
        />

        {/* 3. Main content area expanding full desktop width */}
        <div className="app-content admin-main-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
